import React from 'react';
import { Link, Typography, SxProps, Theme, Tooltip, Button } from '@mui/material';
import { Link as RLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LaunchOutlined } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

const styles: { [key: string]: SxProps } = {
  item: {
    width: '100%',
    height: 48,
    margin: 0,
    display: 'flex',
    color: '#fff',
    padding: '0 20px',
    fontSize: 14,
    fontFamily: 'YuGothic',
    justifyContent: 'space-between',
    alignItems: 'center',
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: '#3b627a',
    },
  },
  font: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  noHover: {
    '&:hover': {
      backgroundColor: '#007B43',
    },
  },
};

interface Item {
  title: string;
  link?: string;
  subNav?: { title: string; link: string; blank?: boolean; id?: string; callback?: () => void; }[];
  blank?: boolean;
  id?: string;
  callback?: () => void;
}

interface LinkWithHrefProps {
  item: Item;
  sx?: SxProps;
  blank?: boolean;
  id?: string;
  onClick?: () => void;
}

const LinkWithHref: React.FC<LinkWithHrefProps> = ({ item, sx, blank, onClick }) => {
  const { t } = useTranslation();
  const handleClick = () => {
    // GA4: メニュー項目のクリックを計測
    sendAnalyticsEvent('navigation_menu', {
      action: 'click_item',
      menu_item: item.title,
      link_url: item.link,
      is_external: !!blank,
    });
    onClick?.();
  };
  return (
    <Link
      component={RLink}
      sx={{ ...styles.item, ...styles.font, ...sx } as SxProps<Theme>}
      style={{ margin: 0, color: '#fff' }}
      to={item.link!}
      target={blank ? '_blank' : ''}
      id={item?.id || ''}
      onClick={handleClick}
    >
      {item.title}
      {item.title === 'Climate Vision' && (
        <>
          <Tooltip
            title={t('common.menu.tooltip.climateVision')}
            PopperProps={{
              style: { maxWidth: '300px' },
            }}
          >
            <HelpOutlineIcon style={{ cursor: 'pointer', marginLeft: 2, fontSize: '14px' }} />
          </Tooltip>
          <LaunchOutlined style={{ fontSize: '18px', marginLeft: '16px' }} />
        </>
      )}
    </Link>
  );
};

const selectedStyle = { backgroundColor: '#3b627a' };

interface SubMenuProps {
  item: Item;
  selectedPathname: string;
  onItemClick?: () => void;
}

const SubMenu: React.FC<SubMenuProps> = ({ item, selectedPathname, onItemClick }) => {
  const [t] = useTranslation();

  return (
    <>
      {item.link && (
        <>
          <LinkWithHref
            sx={
              selectedPathname === item.link &&
              (item?.subNav || []).map(d => d.link).indexOf(selectedPathname) < 0
                ? selectedStyle
                : {}
            }
            item={item}
            blank={item?.blank || false}
            onClick={onItemClick}
          />
        </>
      )}
      {(item?.subNav || []).map((subItem, index) => {
        if (subItem.link === '/prediction/alerts') {
          return (
            <Link
              component={RLink}
              sx={{
                ...styles.item,
                ...([subItem.link, '/prediction/edit_alert'].includes(selectedPathname)
                  ? selectedStyle
                  : {}),
              }}
              style={{
                margin: 0,
                color: '#fff',
                paddingLeft: '40px',
              }}
              to={subItem.link}
              key={index}
              target={subItem.blank ? '_blank' : ''}
              id={subItem?.id || ''}
              onClick={() => {
                // GA4: アラート一覧メニューのクリックを計測
                sendAnalyticsEvent('navigation_menu', {
                  action: 'click_item',
                  menu_item: t(subItem.title),
                  link_url: subItem.link,
                  is_external: !!subItem.blank,
                });
                onItemClick?.();
              }}
            >
              {t(subItem.title)}
            </Link>
          );
        }

        if (subItem.callback) {
          return (
            <Link
              component={Button}
              sx={{
                ...styles.item,
                ...(selectedPathname === subItem.link ? selectedStyle : {}),
              }}
              style={{
                margin: 0,
                color: '#fff',
                paddingLeft: '40px',
              }}
              key={index}
              id={subItem?.id || ''}
              onClick={() => {
                // GA4: チュートリアルなどのコールバックメニューを計測
                sendAnalyticsEvent('navigation_menu', {
                  action: 'click_item',
                  menu_item: t(subItem.title),
                  link_url: subItem.link,
                  is_callback: true,
                });
                subItem.callback?.();
                onItemClick?.();
              }}
            >
              {t(subItem.title)}
            </Link>
          );
        }

        return (
          <Link
            component={RLink}
            sx={{
              ...styles.item,
              ...(selectedPathname === subItem.link ? selectedStyle : {}),
            }}
            style={{
              margin: 0,
              color: '#fff',
              paddingLeft: '40px',
            }}
            to={subItem.link}
            key={index}
            target={subItem.blank ? '_blank' : ''}
            id={subItem?.id || ''}
            onClick={() => {
              // GA4: サブメニュー項目のクリックを計測
              sendAnalyticsEvent('navigation_menu', {
                action: 'click_item',
                menu_item: t(subItem.title),
                link_url: subItem.link,
                is_external: !!subItem.blank,
              });
              onItemClick?.();
            }}
          >
            {t(subItem.title)}
            {subItem.blank ? <LaunchOutlined style={{ fontSize: '18px' }} /> : null}
          </Link>
        );
      })}
    </>
  );
};

export default SubMenu;
